import { Pill } from '@/components/UI/Pill'
import { PillSelect } from '@/components/UI/PillSelect'
import type { PillSelectOption } from '@/components/UI/PillSelect'
import type { SpotTagData } from '@/schemas/v2/spot'
import styles from './SpotFilterRow.module.css'

type SpotFilterRowProps = {
  /** PillSelect 用シートオプション（「すべてのコース」を含む） */
  sheetOptions: PillSelectOption[]
  /** 現在選択中のシート値（空文字 = すべて） */
  selectedSheet: string
  /** シート変更コールバック */
  onSheetChange: (value: string) => void
  /** タグ一覧 */
  tags: SpotTagData[]
  /** 現在選択中のタグID（null = 未選択） */
  selectedTagId: number | null
  /** タグクリックコールバック */
  onTagClick: (tagId: number) => void
  /** コンテナに追加する className（余白のカスタマイズ用） */
  className?: string
}

/**
 * スポット一覧ページのフィルター行コンポーネント。
 * PillSelect（シート選択）と Pill ボタン（タグ絞り込み）を横スクロール行に並べる。
 *
 * - PillSelect はシートが 2 件以上のときのみ表示する
 * - Pill ボタンはタグが 1 件以上のときのみ表示する
 * - 両方 0 件の場合は null を返す
 */
export function SpotFilterRow({
  sheetOptions,
  selectedSheet,
  onSheetChange,
  tags,
  selectedTagId,
  onTagClick,
  className,
}: SpotFilterRowProps) {
  const showPillSelect = sheetOptions.length >= 2
  const showTags = tags.length > 0

  if (!showPillSelect && !showTags) return null

  return (
    <div
      data-testid="spot-filter-row"
      className={`flex items-center gap-2 overflow-x-auto px-4 py-2 ${className ?? ''} ${styles.filterRow}`}
    >
      {showPillSelect && <PillSelect options={sheetOptions} value={selectedSheet} onChange={onSheetChange} />}
      {showTags &&
        tags.map((tag) => {
          const isSelected = selectedTagId === tag.id

          return (
            <button
              key={tag.id}
              type="button"
              className="shrink-0"
              aria-pressed={isSelected}
              aria-label={tag.name}
              onClick={() => onTagClick(tag.id)}
            >
              <Pill type={isSelected ? 'Active' : 'Inactive'} label={tag.name} />
            </button>
          )
        })}
    </div>
  )
}
